import type { ISettingValueMaster } from "@bbj-types/setting";
import { addressSchema } from "@root/utils/resolvers/address";
import { candidateSchema } from "@root/utils/resolvers/candidate";
import { array, boolean, date, mixed, number, object, string } from "yup";
import { missedReason } from "../logics/constants";
import type { IModalActionForm, TMissedReason } from "../logics/types";
import { hasAddressDisplayed, hasAddressRequired, hasStartTimeRequired } from "../logics/utils";
import { jobFollowUpSchema } from "./jobFollowUp";

export const baseSchema = (
  requiredContactDetails: string[],
  hideCandidateField?: boolean,
  hideApproachField?: boolean
) =>
  object({
    title: string().optional(),
    type: object({
      value: string().required(),
      label: string().required(),
    }).required("Ce champ est obligatoire"),
    approach: object({
      _id: string().required(),
      label: string().required(),
      challengeName: string().required(),
    })
      .nullable()
      .when("type", {
        is: (type: ISettingValueMaster | null) => !hideApproachField && type?.metadata?.approachActionType,
        then: (schema) => schema.required("Ce champ est obligatoire"),
        otherwise: (schema) => schema.optional(),
      }),
    challenges: array(
      object({
        _id: string().required(),
        name: string().required(),
      })
    ).required(),
    comments: string().optional(),
    candidate: hideCandidateField ? candidateSchema.nullable() : candidateSchema.required("Ce champ est obligatoire"),
    owner: object({
      _id: string().required(),
      firstName: string().required(),
      lastName: string().required(),
    }).required("Ce champ est obligatoire"),
    contactChannel: object({
      value: string().required(),
      label: string().required(),
    })
      .nullable()
      .when("type", {
        is: (type: ISettingValueMaster | null) => requiredContactDetails.includes(type?.value ?? ""),
        then: (schema) => schema.required("Ce champ est obligatoire"),
      }),
    date: date()
      .nullable()
      .when("type", {
        is: (type: ISettingValueMaster | null) => !type?.metadata?.approachActionType,
        then: (schema) => schema.required("Ce champ est obligatoire"),
        otherwise: (schema) => schema.optional(),
      }),
    executionPeriod: object({
      startDate: date().required("Ce champ est obligatoire"),
      endDate: date().required("Ce champ est obligatoire"),
    })
      .nullable()
      .when("type", {
        is: (type: ISettingValueMaster | null) => type?.metadata?.approachActionType,
        then: (schema) => schema.required("Ce champ est obligatoire"),
        otherwise: (schema) => schema.optional(),
      }),
    startTime: number().when(
      ["type", "endTime", "sendEventToOwner", "status", "shouldScheduleAppointmentReminder"],
      (values, schema) => {
        const type: ISettingValueMaster | null = values[0];
        const endTime: number = values[1];
        const sendEventToOwner: boolean = values[2];
        const status: IModalActionForm["status"] = values[3];
        const shouldScheduleAppointmentReminder: boolean = values[4];
        if (
          hasStartTimeRequired(
            type,
            requiredContactDetails,
            endTime,
            sendEventToOwner,
            status,
            shouldScheduleAppointmentReminder
          )
        ) {
          const requiredSchema = schema.required("Ce champ est obligatoire").typeError("Ce champ est obligatoire");
          if (endTime) {
            return requiredSchema.max(endTime, "L'heure de début de l'action ne peut pas être après l'heure de fin");
          }
          return requiredSchema;
        }
        return schema.transform((value: IModalActionForm["startTime"]): number => value || 0);
      }
    ),
    endTime: number()
      .optional()
      .transform((value: number | "") => value || 0),
    status: mixed<"done" | "dismissed" | "todo" | "missed">()
      .oneOf(["done", "dismissed", "todo", "missed"])
      .required("Ce champ est obligatoire")
      .when(["type", "jobFollowUp.jobSituation"], {
        is: (type: ISettingValueMaster | null, jobSituation: string) => type?.value === "job-follow-up" && jobSituation,
        then: (schema) =>
          schema.test((value, { createError }) => {
            if (value === "todo") {
              return createError({
                path: "status",
                message:
                  'La situation du candidat est renseignée, veuillez passer cette action en "déjà réalisée" ou "manquée"',
              });
            }
            return true;
          }),
      }),
    missedReason: mixed<TMissedReason["value"]>().when("status", {
      is: "missed",
      then: (schema) =>
        schema.oneOf(
          missedReason.map((v) => v.value),
          "Ce champ est obligatoire"
        ),
      otherwise: (schema) => schema.transform(() => ""),
    }),
    timeSpent: number()
      .optional()
      .when("status", {
        is: "todo",
        then: (schema) => schema.transform(() => 0),
        otherwise: (schema) => schema.transform((value: number | "") => value || 0),
      }),
    address: addressSchema
      .nullable()
      .when(["type", "contactChannel", "status", "shouldScheduleAppointmentReminder"], (values, schema) => {
        const [type, contactChannel, status, shouldScheduleAppointmentReminder] = values as [
          ISettingValueMaster | null,
          ISettingValueMaster | null,
          IModalActionForm["status"],
          boolean,
        ];
        if (hasAddressRequired(type, contactChannel)) {
          return schema.required("Ce champ est obligatoire");
        }
        // Le champ n'est plus affiché : on repart de zéro pour ne pas transmettre une adresse
        // saisie puis abandonnée (switch de rappel désactivé, changement de type d'action...).
        if (!hasAddressDisplayed(type, contactChannel, status, shouldScheduleAppointmentReminder)) {
          return schema.transform(() => null);
        }
        return schema;
      }),
    jobFollowUp: jobFollowUpSchema.nullable().when("type", {
      is: (type: ISettingValueMaster | null) => type?.value === "job-follow-up",
      then: (schema) =>
        schema.required().when("status", {
          is: (status: string) => status !== "todo",
          then: (schema) =>
            schema.test((value, { createError }) => {
              if (!value.jobSituation) {
                return createError({
                  path: "jobFollowUp.jobSituation",
                  message: "Ce champ est obligatoire",
                });
              }
              return true;
            }),
        }),
      otherwise: (schema) => schema.transform(() => null),
    }),
    pinned: boolean().default(false),
    sendEventToOwner: boolean().default(false),
    beneficiaryComment: string().optional().nullable(),
    nextAction: boolean().default(false),
    nextActionDate: date()
      .nullable()
      .when("nextAction", {
        is: true,
        then: (schema) => schema.required("Ce champ est obligatoire"),
        otherwise: (schema) => schema.transform(() => null),
      }),
    nextActionStartTime: number()
      .optional()
      .transform((value: number | "") => value || 0),
    shouldScheduleAppointmentReminder: boolean().default(false),
  });
